import { StarIcon } from "@heroicons/react/24/outline";
import { ChevronRightIcon } from "@heroicons/react/24/solid";
import { unwrapConnection } from "@utils/utils";
import clsx from "clsx";
import React, { useEffect, useState } from "react";

import { Badge, Tooltip } from "@fronterahealth/frontera-ui-components";
import { PopoverMenu } from "@fronterahealth/frontera-ui-components/PopoverActionMenu/PopoverActionMenu";

import { getStatusAppearance, getStatusIcon } from "@components/TreatmentPlan/ProgrammingGoalCard/helper";
import { TruncateWithTooltip } from "@components/TruncateWithTooltip";
import { getColorForGoal } from "@pages/VideoSessionsV2/components/MediaTimeline/helper";
import { MomentRow } from "@pages/VideoSessionsV2/components/Moments/VideoMoments/components/MomentRow";
import { TargetRow } from "@pages/VideoSessionsV2/components/Moments/VideoMoments/components/TargetRow";
import { GoalMoment, useVideoSessionData } from "@providers/VideoSessionProviderV2";

interface GoalCardProps {
  goal: GoalMoment;
  onClick: (startTime: number, endTime: number, momentId: string) => void;
  onPinToggle: (goal: GoalMoment) => void;
  isPending: boolean;
  expandAll?: boolean;
}

export const GoalCard: React.FC<GoalCardProps> = ({ goal, onClick, onPinToggle, isPending, expandAll }) => {
  const [isExpanded, setIsExpanded] = useState(expandAll || false);

  const { activeMomentId, activeMomentGoalId, setActiveMomentGoalId, setActiveMomentTargetId } = useVideoSessionData();

  const targets = unwrapConnection(goal.targets) || [];
  const goalMoments = unwrapConnection(goal.mediatargetpredictionSet) || [];

  const totalMoments =
    goalMoments.length +
    targets.reduce((sum, t) => sum + (unwrapConnection(t?.mediatargetpredictionSet)?.length || 0), 0);

  const handleMomentClick = (startTime: number, endTime: number, momentId: string) => {
    onClick(startTime, endTime, momentId);
  };

  useEffect(() => {
    setIsExpanded(expandAll || false);
  }, [expandAll]);

  useEffect(() => {
    if (activeMomentGoalId === goal.id) {
      setIsExpanded(true);
    }
  }, [activeMomentGoalId, goal.id]);

  const Icon = goal.status ? getStatusIcon(goal.status) : React.Fragment;

  return (
    <div className="bg-white border border-limestone-200 rounded-2xl shadow-xs hover:shadow-md transition-shadow duration-200 overflow-hidden">
      <div className="flex items-center justify-between p-3 cursor-pointer" onClick={() => setIsExpanded(!isExpanded)}>
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <div className="w-1 h-6 rounded-full shrink-0" style={{ backgroundColor: getColorForGoal(goal.name!) }} />
          <ChevronRightIcon
            className={clsx(
              "w-4 h-4 text-limestone-600 transition-transform duration-200 shrink-0",
              isExpanded && "rotate-90",
            )}
          />
          <TruncateWithTooltip>
            <h4 className="text-sm font-medium text-limestone-900 flex-1">{goal.name || "Unnamed Goal"}</h4>
          </TruncateWithTooltip>
          <span className="px-2 py-0.5 text-xs font-medium text-limestone-600 bg-limestone-100 rounded-full shrink-0">
            {totalMoments}
          </span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {goal.status && (
            <Tooltip text={goal.status} showInfoIcon={false}>
              <Badge
                text={
                  <div className="flex flex-row gap-1">
                    <Icon className="h-3 w-3" />
                  </div>
                }
                className="text-sm"
                appearance={getStatusAppearance(goal.status)}
              />
            </Tooltip>
          )}
          <PopoverMenu
            menuItems={[
              {
                Icon: StarIcon,
                label: goal.isPinned ? "Unpin goal" : "Pin goal",
                callback: () => !isPending && onPinToggle(goal),
              },
            ]}
          />
        </div>
      </div>

      <div className={clsx("overflow-hidden transition-all duration-300", isExpanded ? "max-h-[800px]" : "max-h-0")}>
        {/* Targets */}
        {targets.length > 0 && (
          <div>
            {targets.map((target) => (
              <TargetRow
                key={target.id}
                target={target}
                onClick={(start: number, end: number, momentId: string) => {
                  setActiveMomentGoalId(goal.id);
                  setActiveMomentTargetId(target.id);
                  handleMomentClick(start, end, momentId);
                }}
                expandAll={expandAll}
              />
            ))}
          </div>
        )}

        {/* Goal-level moments */}
        {goalMoments.length > 0 && (
          <div className="border-t border-limestone-200">
            <div className="px-3 py-3">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-sm font-medium text-limestone-700">Goal Moments</span>
                <span className="px-2 py-0.5 text-xs font-medium text-limestone-600 bg-limestone-100 rounded-full">
                  {goalMoments.length}
                </span>
              </div>
              <div className="space-y-1">
                {goalMoments.map(
                  (moment) =>
                    moment && (
                      <MomentRow
                        key={moment.id}
                        moment={moment}
                        isActive={moment.id === activeMomentId}
                        onClick={() => {
                          setActiveMomentGoalId(goal.id);
                          handleMomentClick(moment.startTime, moment.endTime, moment.id);
                        }}
                      />
                    ),
                )}
              </div>
            </div>
          </div>
        )}

        {/* Empty state */}
        {targets.length === 0 && goalMoments.length === 0 && (
          <div className="px-4 py-3 text-sm text-limestone-400 italic border-t border-limestone-200">
            No targets or moments for this goal
          </div>
        )}
      </div>
    </div>
  );
};
